import { estimateStageDurationMs } from '@src/config/processingEstimates';
import { useEffect, useRef, useState } from 'react';

import {
  isStoryCompressedVideoReady,
  isStoryOriginalVideoReady,
  isStoryReady,
  isStoryTranscriptionReady,
} from '../utils';

import type { StoryUploadPhase } from '@src/stores-v2/UserStoryUploadFlowStore';
import type { DashboardStory } from '@src/types.ts/story';

export type UploadPhaseStory = Pick<
  DashboardStory,
  'id' | 'useAws' | 'originalVideo' | 'uploadLog' | 'transcription'
>;

const MILESTONE_START = 50;
const MILESTONE_OPTIMIZED = 70;
const MILESTONE_ASSETS = 90;
const MILESTONE_READY = 100;

const TICK_MS = 500;
// Decay rate for the per-segment progress curve. Larger values reach the cap
// faster but punish overrun more harshly; 2 means we hit ~86% of the segment
// at the expected ETA and still creep toward the cap when the actual stage
// runs longer than predicted.
const SEGMENT_DECAY_RATE = 2;

type Milestone =
  | { level: typeof MILESTONE_START; next: typeof MILESTONE_OPTIMIZED }
  | { level: typeof MILESTONE_OPTIMIZED; next: typeof MILESTONE_ASSETS }
  | { level: typeof MILESTONE_ASSETS; next: typeof MILESTONE_READY }
  | { level: typeof MILESTONE_READY; next: null };

function getMilestone(story: UploadPhaseStory): Milestone {
  const optimized = isStoryOriginalVideoReady(story);
  const transcribed = isStoryTranscriptionReady(story);
  const compressed = isStoryCompressedVideoReady(story);
  if (optimized && transcribed && compressed) {
    return { level: MILESTONE_READY, next: null };
  }
  if (optimized && (transcribed || compressed)) {
    return { level: MILESTONE_ASSETS, next: MILESTONE_READY };
  }
  if (optimized) {
    return { level: MILESTONE_OPTIMIZED, next: MILESTONE_ASSETS };
  }
  return { level: MILESTONE_START, next: MILESTONE_OPTIMIZED };
}

function estimateSegmentMs(
  milestone: Milestone['level'],
  videoDurationSec: number | undefined,
  fileSizeBytes: number | undefined,
): number {
  if (milestone === MILESTONE_START) {
    return estimateStageDurationMs('startToOptimized', { fileSizeBytes });
  }
  if (milestone === MILESTONE_OPTIMIZED) {
    return estimateStageDurationMs('optimizedToAssets', { videoDurationSec });
  }
  if (milestone === MILESTONE_ASSETS) {
    return estimateStageDurationMs('assetsToReady', {});
  }
  return 0;
}

function getPhaseFileSizeBytes(phase: StoryUploadPhase): number | undefined {
  if (
    phase.phase === 'queued' ||
    phase.phase === 'uploading' ||
    phase.phase === 'processing' ||
    phase.phase === 'complete'
  ) {
    return phase.fileSizeBytes;
  }
  return undefined;
}

/**
 * Returns an estimated processing-progress value (50–100) for a story whose
 * server-side processing is in flight, expressed in the unified
 * upload+processing bar space (where upload occupies 0–50). Snaps to stage
 * milestones as backend signals land in Dato, and smoothly interpolates
 * between them using elapsed-time vs. estimated segment duration. Monotonic
 * within a component instance (never regresses); capped just shy of the next
 * milestone until the real signal arrives.
 *
 * Active whenever the upload-flow phase is 'processing' OR has already moved
 * to 'complete' (kickoff API returned) but the story does not yet have all
 * its assets. Returns `undefined` when neither condition holds.
 */
export function useProcessingProgress(
  story: UploadPhaseStory,
  phase: StoryUploadPhase,
): number | undefined {
  const [, tick] = useState(0);

  // Per-instance state — no module-level Maps, so memory and test isolation
  // come for free with the component lifecycle.
  const milestoneEnteredAtRef = useRef<{ level: number; at: number } | null>(
    null,
  );
  const lastPercentRef = useRef(0);

  const storyFullyReady = isStoryReady(story);
  const isActive =
    phase.phase === 'processing' ||
    (phase.phase === 'complete' && !storyFullyReady);

  useEffect(() => {
    if (!isActive) return;
    const id = window.setInterval(() => tick(t => t + 1), TICK_MS);
    return () => window.clearInterval(id);
  }, [isActive]);

  const milestone = getMilestone(story);

  // Time-capturing writes happen in commit-phase effects, NOT during render.
  // StrictMode double-renders would otherwise overwrite the captured timestamp
  // with a later `Date.now()` and bias elapsed-time computations downward.

  // Reset the ratchet when isActive flips false→true (e.g. retry after error).
  useEffect(() => {
    if (isActive) {
      // On every fresh activation, start the percent ratchet from zero and
      // discard any stale milestone timestamp from a previous attempt.
      lastPercentRef.current = 0;
      milestoneEnteredAtRef.current = null;
    }
  }, [isActive]);

  // Capture the milestone-entry timestamp in a commit-phase effect so each
  // real commit writes exactly once (StrictMode-safe).
  useEffect(() => {
    if (!isActive) return;
    const tracked = milestoneEnteredAtRef.current;
    if (!tracked || tracked.level !== milestone.level) {
      milestoneEnteredAtRef.current = {
        level: milestone.level,
        at: Date.now(),
      };
    }
  }, [isActive, milestone.level]);

  if (!isActive) return undefined;

  // The first activation render runs before the effects above commit, so the
  // ref may be null. Return the milestone floor in that case — it's the
  // correct lower bound and matches the eventual interpolation start.
  const tracked = milestoneEnteredAtRef.current;
  if (!tracked || tracked.level !== milestone.level) {
    const floor = Math.max(lastPercentRef.current, milestone.level);
    lastPercentRef.current = floor;
    return floor;
  }
  const enteredAt = tracked.at;

  if (milestone.next === null) {
    const next = Math.max(lastPercentRef.current, MILESTONE_READY);
    lastPercentRef.current = next;
    return next;
  }

  const videoDurationSec = story.originalVideo?.video?.duration;
  const fileSizeBytes = getPhaseFileSizeBytes(phase);
  const etaMs = estimateSegmentMs(
    milestone.level,
    videoDurationSec,
    fileSizeBytes,
  );
  const elapsedMs = Date.now() - enteredAt;
  // Exponential decay: at elapsed = etaMs we're at ~86% of the segment; even
  // when the actual stage runs much longer than estimated, the bar continues
  // to creep toward the cap rather than freezing dead at a soft asymptote.
  const progressFraction =
    etaMs > 0 ? 1 - Math.exp((-SEGMENT_DECAY_RATE * elapsedMs) / etaMs) : 0;

  const interpolated =
    milestone.level + (milestone.next - milestone.level) * progressFraction;
  // Hard cap strictly below the next milestone so it never touches until the
  // backend signal arrives.
  const capped = Math.min(interpolated, milestone.next - 1);

  const percent = Math.max(lastPercentRef.current, capped);
  lastPercentRef.current = percent;
  return percent;
}

export default useProcessingProgress;
